import { axiosInstance } from './axiosConfig';
import { handleRequestWithAuth } from './refreshService';

const API_URL = 'cita-correspondencia/';

// Interfaces para la configuración de correspondencia
export interface ServicioInfo {
    id: number;
    nombre: string;
}

// Interfaces principales
export interface CitaCorrespondencia {
    id: number;
    cita: number;
    cita_codigo: string;
    cliente_nombre?: string;
    cliente_email?: string;
    correspondencia_config: number;
    servicio: ServicioInfo;
    habilitado: boolean;
    fecha_activacion: string;
}

export interface CitaCorrespondenciaCreate {
    cita: number;
    correspondencia_config: number;
    habilitado?: boolean;
}

export interface CitaCorrespondenciaUpdate {
    correspondencia_config?: number;
    habilitado?: boolean;
}

// Operaciones CRUD básicas
export const fetchCitaCorrespondencia = async (): Promise<CitaCorrespondencia[]> => {
    return handleRequestWithAuth(async () => {
        const response = await axiosInstance.get(API_URL);
        return response.data;
    });
};

export const createCitaCorrespondencia = async (data: CitaCorrespondenciaCreate): Promise<CitaCorrespondencia> => {
    return handleRequestWithAuth(async () => {
        const response = await axiosInstance.post(API_URL, data);
        return response.data;
    });
};

export const updateCitaCorrespondencia = async (id: number, data: CitaCorrespondenciaUpdate): Promise<CitaCorrespondencia> => {
    return handleRequestWithAuth(async () => {
        const response = await axiosInstance.patch(`${API_URL}${id}/`, data);
        return response.data;
    });
};

export const deleteCitaCorrespondencia = async (id: number): Promise<void> => {
    return handleRequestWithAuth(async () => {
        await axiosInstance.delete(`${API_URL}${id}/`);
    });
};

export const getCitaCorrespondenciaById = async (id: number): Promise<CitaCorrespondencia> => {
    return handleRequestWithAuth(async () => {
        const response = await axiosInstance.get(`${API_URL}${id}/`);
        return response.data;
    });
};

// Filtros específicos
export const fetchCitaCorrespondenciaFiltrados = async (filtros: {
    cita?: number;
    habilitado?: boolean;
}): Promise<CitaCorrespondencia[]> => {
    return handleRequestWithAuth(async () => {
        const params: Record<string, any> = {};
        
        if (filtros.cita !== undefined) {
            params.cita = filtros.cita;
        }
        
        if (filtros.habilitado !== undefined) {
            params.habilitado = filtros.habilitado;
        }
        
        const response = await axiosInstance.get(API_URL, { params });
        return response.data;
    });
};

// Obtener configuración de correspondencia por cita específica
export const fetchCitaCorrespondenciaPorCita = async (cita_id: number): Promise<CitaCorrespondencia | null> => {
    return handleRequestWithAuth(async () => {
        try {
            const response = await axiosInstance.get(API_URL, { 
                params: { cita: cita_id } 
            });
            // Retornamos el primero si existe, o null si no hay resultados
            return response.data.length > 0 ? response.data[0] : null;
        } catch (error: any) {
            if (error.response?.status === 404) {
                return null;
            }
            console.error('Error fetching correspondencia por cita:', error);
            throw error;
        }
    });
};

// Activar/desactivar correspondencia para una cita
export const toggleHabilitadoCitaCorrespondencia = async (id: number): Promise<CitaCorrespondencia> => {
    return handleRequestWithAuth(async () => {
        // Primero obtenemos el estado actual
        const current = await axiosInstance.get(`${API_URL}${id}/`);
        const newState = !current.data.habilitado;
        
        // Actualizamos
        const response = await axiosInstance.patch(`${API_URL}${id}/`, {
            habilitado: newState
        });
        return response.data;
    });
};

// Verificar si una cita ya tiene correspondencia configurada
export const verificarCitaTieneCorrespondencia = async (cita_id: number): Promise<boolean> => {
    return handleRequestWithAuth(async () => {
        const response = await axiosInstance.get(API_URL, { 
            params: { cita: cita_id } 
        });
        return response.data.length > 0;
    });
};

// Obtener todas las correspondencias configuradas para un servicio específico
export const fetchCitaCorrespondenciaPorServicio = async (servicio_id: number): Promise<CitaCorrespondencia[]> => {
    return handleRequestWithAuth(async () => {
        try {
            // Nota: Esta función podría necesitar un endpoint personalizado en el backend
            // Por ahora filtramos desde el frontend si no hay endpoint específico
            const all = await axiosInstance.get(API_URL);
            return all.data.filter((item: CitaCorrespondencia) => 
                item.servicio.id === servicio_id
            );
        } catch (error) {
            console.error('Error fetching correspondencia por servicio:', error);
            throw error;
        }
    });
};

// Estadísticas/resumen
export interface ResumenCorrespondencia {
    total_configuraciones: number;
    activas: number;
    inactivas: number;
    por_servicio: Array<{
        servicio_id: number;
        servicio_nombre: string;
        cantidad: number;
    }>;
}

export const fetchResumenCorrespondencia = async (): Promise<ResumenCorrespondencia> => {
    return handleRequestWithAuth(async () => {
        const all = await axiosInstance.get(API_URL);
        const data: CitaCorrespondencia[] = all.data;
        
        const resumen: ResumenCorrespondencia = {
            total_configuraciones: data.length,
            activas: data.filter(item => item.habilitado).length,
            inactivas: data.filter(item => !item.habilitado).length,
            por_servicio: []
        };
        
        // Agrupar por servicio
        const servicioMap = new Map<number, { nombre: string, cantidad: number }>();
        
        data.forEach(item => {
            const servicioId = item.servicio.id;
            if (!servicioMap.has(servicioId)) {
                servicioMap.set(servicioId, {
                    nombre: item.servicio.nombre,
                    cantidad: 0
                });
            }
            const servicio = servicioMap.get(servicioId)!;
            servicio.cantidad++;
        });
        
        // Convertir map a array
        servicioMap.forEach((value, key) => {
            resumen.por_servicio.push({
                servicio_id: key,
                servicio_nombre: value.nombre,
                cantidad: value.cantidad
            });
        });
        
        return resumen;
    });
};